var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from "react";
import { getIdenticonsVariableOverrides } from "../identicons";
import { Label } from "../label";
import { useSidebarContext } from "../sidebar/sidebar-context";
import { makeStyles } from "../styles";
import clsx from "../utils/clsx";
var SidebarLinkComponentName = "QuantumSidebarLink";
var useSidebarLinkStyles = makeStyles(function (theme) { return ({
    root: {
        "& $title, $availabilityLabel, $decoration, $endIcon": {
            opacity: 1,
            willChange: "opacity",
            transition: theme.transitions.create("opacity", {
                easing: theme.transitions.easing.easeIn,
                duration: theme.transitions.duration.enteringScreen,
                delay: theme.transitions.duration.enteringScreen / 2,
            }),
        },
    },
    collapsed: {
        "& $title, $availabilityLabel, $decoration, $endIcon": {
            opacity: 0,
            transition: theme.transitions.create("opacity", {
                easing: theme.transitions.easing.easeIn,
                duration: theme.transitions.duration.leavingScreen,
                delay: 0,
            }),
        },
    },
    link: __assign(__assign(__assign(__assign({}, theme.typography.body2), { borderRadius: theme.shape.borderRadius, textDecoration: "none", lineHeight: theme.typography.pxToRem(20), width: "100%", fontWeight: theme.typography.fontWeightMedium, padding: theme.spacing(1), display: "flex", alignItems: "center", color: theme.palette.text.primary, cursor: "pointer", background: "transparent", border: "none", transition: theme.transitions.create(["color"], { duration: theme.transitions.duration.shortest }) }), getIdenticonsVariableOverrides(theme, "default")), { "&:hover": __assign({ color: theme.palette.primary.main, textDecoration: "none" }, getIdenticonsVariableOverrides(theme, "primary")), "&:focus": __assign({ outline: "none" }, theme.mixins.focusRing()) }),
    secondary: __assign(__assign({}, theme.typography.caption), { lineHeight: theme.typography.pxToRem(22.4), color: theme.palette.text.secondary, paddingTop: theme.spacing(0.5), paddingBottom: theme.spacing(0.5), "& $startIcon": {
            fontSize: theme.typography.pxToRem(16),
        } }),
    active: __assign({ color: theme.palette.primary.main }, getIdenticonsVariableOverrides(theme, "primary")),
    title: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        marginLeft: theme.spacing(1.25),
        minWidth: theme.typography.pxToRem(32),
    },
    icon: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        "& > svg": {
            width: "1em",
            height: "1em",
            transition: theme.transitions.create(["fill", "transform"], { duration: theme.transitions.duration.short }),
        },
    },
    startIcon: {
        width: theme.typography.pxToRem(20),
        fontSize: theme.typography.pxToRem(20),
    },
    availabilityLabel: {
        marginLeft: theme.spacing(1),
    },
    decoration: {
        marginLeft: theme.spacing(1),
    },
    endIcon: {
        marginLeft: "auto",
        color: theme.mixins.shadeOfColor("neutral", "base"),
    },
}); }, { name: SidebarLinkComponentName });
var getFeatureAvailabilityLabelProps = function (featureAvailability) {
    var labelProps = {
        color: "primary",
        variant: "default",
    };
    switch (featureAvailability) {
        case "beta":
            labelProps.children = "Beta";
            break;
        case "first":
            labelProps.children = "First";
            break;
        case "new":
            labelProps.variant = "filled";
            labelProps.children = "New";
            break;
    }
    return labelProps;
};
export var SidebarLink = React.forwardRef(function (props, ref) {
    var classes = useSidebarLinkStyles(props);
    var sidebarCtx = useSidebarContext();
    var title = props.title, startIcon = props.startIcon, endIcon = props.endIcon, featureAvailability = props.featureAvailability, className = props.className, isActive = props.isActive, decoration = props.decoration, titleId = props.titleId, _a = props.variant, variant = _a === void 0 ? "primary" : _a, _b = props.component, LinkComponent = _b === void 0 ? "a" : _b, propClasses = props.classes, children = props.children, linkProps = __rest(props, ["title", "startIcon", "endIcon", "featureAvailability", "className", "isActive", "decoration", "titleId", "variant", "component", "classes", "children"]);
    return (React.createElement("li", { className: clsx(classes.root, !sidebarCtx.expanded && classes.collapsed, className) },
        React.createElement(LinkComponent, __assign({ ref: ref, title: title, className: clsx(classes.link, variant === "secondary" && classes.secondary, isActive && classes.active), "aria-current": isActive ? "page" : undefined }, linkProps),
            React.createElement("span", { className: clsx(classes.icon, classes.startIcon) }, startIcon),
            React.createElement("span", { id: titleId, className: classes.title }, title),
            !!featureAvailability && (React.createElement(Label, __assign({ className: classes.availabilityLabel }, getFeatureAvailabilityLabelProps(featureAvailability)))),
            !!decoration && React.createElement("span", { className: classes.decoration }, decoration),
            !!endIcon && React.createElement("span", { className: clsx(classes.icon, classes.endIcon) }, endIcon)),
        children));
});
